import { NavLink } from 'react-router-dom';
import styles from './TabBar.module.css';

/** The mockup's original 4 tabs (Home / Refine / Get Found (Profiles) / Jobs)
 *  PLUS Track — a 5th tab this brief adds for the applications board (the
 *  mockup's own contract has no Track screen; the brief's explicit ask is
 *  "a Track tab/page", and apps/web already proves the applications board as
 *  its own top-level nav item — see apps/web/src/pages/Tracker.tsx — so a 5th
 *  tab here mirrors an EXISTING product surface rather than inventing new
 *  navigation). Icons transcribed verbatim from the mockup's inline SVGs (TAB
 *  BAR section) for the original 4; Track's icon is a new addition in the
 *  same stroke-based style (1.7 stroke width, 24x24, currentColor,
 *  aria-hidden) so it reads as part of the same icon family. */
const TABS = [
  {
    to: '/',
    end: true,
    label: 'Home',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 11l9-7 9 7M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/refine',
    end: false,
    label: 'Refine',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M21 12a8 8 0 0 1-11.5 7.2L4 20.5l1.3-5.2A8 8 0 1 1 21 12Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/profiles',
    end: false,
    label: 'Get Found',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </svg>
    ),
  },
  {
    to: '/jobs',
    end: false,
    label: 'Jobs',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    ),
  },
  {
    to: '/track',
    end: false,
    label: 'Track',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 19V10M4 19h16M4 19V5M12 19v-6M20 19v-9"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
] as const;

/** LANE STORY-TOUR: the `data-tour` value for the Refine tab specifically —
 *  the ONE tab the guided coach tour's 'get-to-refine' step anchors on (see
 *  lib/storyTour.ts's STORY_TOUR_ANCHOR). Applied to whichever tab's `to`
 *  matches, on BOTH the bottom bar and the desktop side rail below, so the
 *  tour can highlight the real, currently-visible nav affordance regardless
 *  of viewport width rather than assuming one or the other is present. */
function tourAnchorFor(to: string): { 'data-tour'?: string } {
  return to === '/refine' ? { 'data-tour': 'tab-refine' } : {};
}

/** The bottom tab bar (mobile / the shell's own footer, <1024px). Hidden
 *  entirely while onboarding is showing (showTabs=false in the contract). */
export function TabBar() {
  return (
    <nav className={styles.bar} aria-label="Primary">
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) => (isActive ? `${styles.tab} ${styles.active}` : styles.tab)}
          {...tourAnchorFor(tab.to)}
        >
          {tab.icon}
          <span className={styles.label}>{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** The ≥1024px side rail. Same 4 tabs, vertical, sitting beside the centered
 *  phone-width column per the brief's desktop layout. Rendered unconditionally;
 *  CSS hides it below the breakpoint (kept in the DOM rather than JS-toggled
 *  so there is no layout-shift flash on resize). */
export function SideTabRail() {
  return (
    <nav className={styles.rail} aria-label="Primary">
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            isActive ? `${styles.railTab} ${styles.active}` : styles.railTab
          }
          {...tourAnchorFor(tab.to)}
        >
          {tab.icon}
          <span className={styles.label}>{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
